import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Page,
  Card,
  PageActions,
  Text,
  Loading,
  FormLayout,
  TextField,
  Toast,
  Select,
  DropZone,
  Thumbnail,
  InlineStack,
  InlineError,
  Modal,
} from "@shopify/polaris";
import axios from "../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import LoadingScreen from "../../Components/LoadingScreen";
import TagsInput from "react-tagsinput";
import "react-tagsinput/react-tagsinput.css";

const AddEditAchievement = ({ mode, url }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSaving, setIsSaving] = useState(mode === "edit");

  const [item, setItem] = useState({
    title: "",
    description: "",
    text: "",
    tags: [],
    date: "",
    status: "",
    type: "",
    timeline: "", // added timeline
    image: "",
    country: "",
  });

  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState();
  const [isImageUploading, setIsImageUploading] = useState(false);
  const imageKey = useRef("");

  const handleChange = useCallback((field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((a) => !a), []);
  const toastMarkup = active ? (
    <Toast
      content="Changes have been saved successfully"
      onDismiss={toggleActive}
    />
  ) : null;

  const [activeDelete, setActiveDelete] = useState(false);
  const toggleDeleteModal = useCallback(() => setActiveDelete((a) => !a), []);

  const handleYesDelete = () => {
    setIsSaving(true);
    axios
      .delete(`/achievements/${id}`)
      .then(() => navigate(`/admin/achievements`))
      .catch(console.error)
      .finally(() => setIsSaving(false));
  };

  const handleDropZone = useCallback(
    (_dropFiles, acceptedFiles) => setFiles(acceptedFiles[0]),
    []
  );

  const fileUpload = !files && <DropZone.FileUpload />;
  const uploadedFiles = files && (
    <InlineStack gap="200" align="start" blockAlign="center">
      <div className="maxtextwidth">
        {files.name}{" "}
        <Text variant="bodySm" as="p">
          {files.size} bytes
        </Text>
      </div>
    </InlineStack>
  );

  const sendImage = (files) => {
    setIsImageUploading(true);
    const form_data = new FormData();
    form_data.append("entity_type", "achievement");
    form_data.append("image", files[files.length - 1]);

    axios
      .post(`/s3/save-file`, form_data)
      .then((result) => {
        imageKey.current = result.data.data.file_key;
        setItem({
          ...item,
          image: window.URL.createObjectURL(files[files.length - 1]),
        });
      })
      .catch(console.error)
      .finally(() => setIsImageUploading(false));
  };

  useEffect(() => {
    if (mode === "edit") {
      axios
        .get(`/achievements/${id}`)
        .then((res) => {
          const data = res?.data?.data || {};
          setItem({
            title: data?.title || "",
            description: data?.description || "",
            text: data?.text || "",
            tags: data?.tags || [],
            date: data?.date || "",
            status: data?.status || "",
            type: data?.type || "",
            timeline: data?.timeline || "", // load timeline
            image: data?.image_url || "",
            country: data?.country || "",
          });
          setIsSaving(false);
        })
        .catch(console.error);
    }
  }, []);

  const validateFields = () => {
    let hasError = false;
    const newErrors = {};

    ["title", "date", "status", "type", "timeline", "country"].forEach(
      (field) => {
        if (
          !item[field] ||
          (field === "tags"
            ? item.tags.length === 0
            : item[field].toString().trim() === "")
        ) {
          newErrors[field] = "This field is required";
          hasError = true;
        }
      }
    );

    setErrors(newErrors);
    return !hasError;
  };

  function handleSave() {
    if (!validateFields()) return;
    setIsSaving(true);

    const formData = new FormData();
    mode === "edit" && formData.append("_method", "PATCH");
    formData.append("title", item.title);
    formData.append("description", item.description);
    formData.append("text", item.text);
    item.tags.forEach((tag) => {
      formData.append("tags[]", tag);
    });
    formData.append("date", item.date);
    formData.append("status", item.status);
    formData.append("type", item.type);
    formData.append("timeline", item.timeline); // append timeline
    formData.append("country", item.country);
    imageKey.current && formData.append("image", imageKey.current);

    axios
      .post(mode === "new" ? "/achievements" : `/achievements/${id}`, formData)
      .then(() => {
        mode === "new" ? navigate("/admin/achievements") : toggleActive();
      })
      .catch((err) => console.error(err))
      .finally(() => setIsSaving(false));
  }
  const [optionsCountries, setOptionsCountries] = useState([]);
  useEffect(() => {
    axios
      .get("/settings/countries")
      .then((res) => {
        const countriesData = res?.data?.data || [];
        const options = [
          { label: "All", value: "All" },
          ...countriesData.map((country) => ({
            label: country.name,
            value: country.name,
          })),
        ];
        setOptionsCountries(options);
      })
      .catch((err) => console.log(err));
  }, []);
  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Achievement`}
      backAction={{ onAction: () => navigate(`/admin/achievements`) }}
    >
      {isSaving && <Loading />}

      <Card sectioned>
        <FormLayout>
          {/* Image */}
          <Text as="p" fontWeight="regular">
            Image
          </Text>
          <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <div style={{ float: "left" }}>
              <Thumbnail size="large" source={item.image ? item.image : ""} />
            </div>
            <div style={{ width: "100%", marginLeft: "10px" }}>
              <DropZone
                onDrop={handleDropZone}
                onDropAccepted={sendImage}
                allowMultiple={false}
              >
                {uploadedFiles}
                {fileUpload}
              </DropZone>
            </div>
          </div>
          <InlineError message={errors.image} />

          {/* Title */}
          <TextField
            label="Title"
            value={item.title}
            onChange={(value) => handleChange("title", value)}
            error={errors.title}
          />
          <Select
            label="Country"
            options={optionsCountries}
            value={item.country}
            onChange={(value) => handleChange("country", value)}
            placeholder="Please select"
            error={errors?.country}
          />

          {/* Description */}
          <TextField
            label="Description"
            value={item.description}
            onChange={(value) => handleChange("description", value)}
            multiline={3}
            error={errors.description}
          />

          {/* Text (optional) */}
          <TextField
            label="Text"
            value={item.text}
            onChange={(value) => handleChange("text", value)}
            multiline={3}
          />

          {/* Tags */}
          <div style={{ marginBottom: "16px" }}>
            <label className="Polaris-Label">
              Tags
              {errors.tags && (
                <Text variant="bodySm" color="critical">
                  {errors.tags}
                </Text>
              )}
            </label>
            <TagsInput
              value={item.tags}
              onChange={(tags) => handleChange("tags", tags)}
            />
          </div>

          {/* Date */}
          <TextField
            label="Date"
            type="date"
            value={item.date}
            onChange={(value) => handleChange("date", value)}
            error={errors.date}
          />

          {/* Status */}
          <Select
            label="Status"
            options={[
              { label: "Pending", value: "pending" },
              { label: "In Progress", value: "in_progress" },
              { label: "Achieved", value: "achieved" },
            ]}
            value={item.status}
            onChange={(value) => handleChange("status", value)}
            error={errors.status}
            placeholder="Please select"
          />

          {/* Type */}
          <Select
            label="Type"
            options={[
              { label: "Sales", value: "sales" },
              { label: "App", value: "app" },
              { label: "Market", value: "market" },
              { label: "Revenue", value: "revenue" },
              { label: "Product", value: "product" },
              { label: "Marketing", value: "marketing" },
              { label: "Customer Success", value: "customer_success" },
              { label: "Operations", value: "operations" },
              { label: "Team", value: "team" },
              { label: "Innovation", value: "innovation" },
              { label: "Finance", value: "finance" },
            ]}
            value={item.type}
            onChange={(value) => handleChange("type", value)}
            error={errors.type}
            placeholder="Please select"
          />

          {/* Timeline */}
          <Select
            label="Timeline"
            options={[
              { label: "YTD", value: "YTD" },
              { label: "Q1", value: "Q1" },
              { label: "Q2", value: "Q2" },
              { label: "Q3", value: "Q3" },
              { label: "Q4", value: "Q4" },
            ]}
            value={item.timeline}
            onChange={(value) => handleChange("timeline", value)}
            error={errors.timeline}
            placeholder="Please select"
          />
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit"
            ? [
                {
                  content: "Delete",
                  destructive: true,
                  onAction: () => setActiveDelete(true),
                  loading: isSaving,
                },
              ]
            : []
        }
      />

      <Modal
        open={activeDelete}
        onClose={toggleDeleteModal}
        title="Delete Item"
        primaryAction={{
          content: "Yes",
          onAction: handleYesDelete,
          loading: isSaving,
        }}
        secondaryActions={[
          {
            content: "No",
            onAction: toggleDeleteModal,
            loading: isSaving,
          },
        ]}
      >
        <Modal.Section>
          <Text>Are you sure you want to delete this item?</Text>
        </Modal.Section>
      </Modal>

      {toastMarkup}
      {isImageUploading && <LoadingScreen />}
    </Page>
  );
};

export default AddEditAchievement;
